import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV_ITEMS = [
  { label: "Home", to: "/", id: "home" },
  { label: "Academics", to: "/academics", id: "academics" },
  { label: "Campus Life", to: "/campus-life", id: "campus-life" },
  { label: "Gallery", to: "/gallery", id: "gallery" },
  { label: "Admissions", to: "/admissions", id: "admissions" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const smoothTo = (target) => {
    if (window.__lenis) {
      window.__lenis.scrollTo(target, { duration: 1.4 });
    } else if (target === 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const goContact = (e) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => smoothTo("#contact"), 600);
    } else {
      smoothTo("#contact");
    }
  };

  const goHome = () => {
    setOpen(false);
    if (location.pathname === "/") {
      smoothTo(0);
    }
  };

  const linkCls = (active) =>
    `relative pb-1 text-[13px] font-medium uppercase tracking-[0.14em] transition-colors duration-300 ${
      active ? "text-white" : "text-white/70 hover:text-white"
    }`;

  const underline = (active) => (
    <span
      aria-hidden="true"
      className={`absolute -bottom-0.5 left-0 h-[2px] bg-[#F47221] transition-all duration-300 ${
        active ? "w-full" : "w-0"
      }`}
    />
  );

  return (
    <header data-testid="navbar" className="fixed inset-x-0 top-0 z-50">
      <div
        aria-hidden="true"
        className={`absolute inset-x-0 top-0 h-24 transition-colors duration-500 md:h-28 ${
          scrolled || open
            ? "bg-gradient-to-b from-[#0A2647]/95 via-[#0A2647]/60 to-transparent"
            : "bg-gradient-to-b from-[#0A2647]/55 to-transparent"
        }`}
      />
      <nav className="relative mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:h-20 md:px-8">
        <Link
          to="/"
          onClick={goHome}
          data-testid="nav-brand-link"
          aria-label="Val-Hue High School — Home"
          className="flex items-center gap-3"
        >
          <span className="block h-10 w-10 overflow-hidden rounded-full ring-1 ring-white/40">
            <img
              src="/logo.png"
              alt="Val-Hue High School logo"
              className="h-full w-full scale-[2.9] object-cover object-[49%_49%]"
            />
          </span>
          <span className="font-display text-sm font-semibold tracking-[0.22em] text-white md:text-base">
            VAL-HUE
          </span>
        </Link>

        <ul className="hidden items-center gap-8 lg:flex">
          {NAV_ITEMS.map((item) => {
            const active = item.to === "/" && location.pathname === "/";
            return (
              <li key={item.id}>
                <Link
                  to={item.to}
                  onClick={item.to === "/" ? goHome : undefined}
                  data-testid={`nav-${item.id}-link`}
                  className={linkCls(active)}
                  aria-current={active ? "page" : undefined}
                >
                  {item.label}
                  {underline(active)}
                </Link>
              </li>
            );
          })}
          <li>
            <a
              href="#contact"
              onClick={goContact}
              data-testid="nav-contact-link"
              className={linkCls(false)}
            >
              Contact Us
              {underline(false)}
            </a>
          </li>
        </ul>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-menu-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          className="text-white lg:hidden"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {open && (
      <div
        data-testid="nav-mobile-menu"
        className="fixed inset-0 top-16 z-40 bg-[#0A2647] lg:hidden"
      >
          <ul className="flex flex-col gap-1 px-8 pt-10">
            {NAV_ITEMS.map((item) => {
              const active = item.to === "/" && location.pathname === "/";
              return (
                <li key={item.id}>
                  <Link
                    to={item.to}
                    onClick={item.to === "/" ? goHome : () => setOpen(false)}
                    data-testid={`nav-mobile-${item.id}-link`}
                    className={`block border-b border-white/10 py-4 font-display text-2xl font-semibold tracking-wide transition-colors ${
                      active ? "text-[#F47221]" : "text-white hover:text-[#D6EBEE]"
                    }`}
                  >
                    {item.label}
                  </Link>
                </li>
              );
            })}
            <li>
              <a
                href="#contact"
                onClick={goContact}
                data-testid="nav-mobile-contact-link"
                className="block border-b border-white/10 py-4 font-display text-2xl font-semibold tracking-wide text-white transition-colors hover:text-[#D6EBEE]"
              >
                Contact Us
              </a>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
};
